/**
 * useInvestmentContext Hook
 *
 * Extracts current investment context from URL and provides it for AI chat.
 * Supports both single investment detail pages and the investments list page.
 * On the home page, also loads costs, benefits, and sponsors for richer AI context.
 */

import {
  useInvestment,
  useInvestments,
  useMilestones,
  useCosts,
  useBenefits,
  useExtractedTexts,
  costsService,
  benefitsService,
  sponsorsService,
  resourcesService,
  costKeys,
  benefitKeys,
  sponsorKeys,
  resourceKeys,
} from '@medsuite/api-client';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import type { Investment, AttachmentExtractedText } from '@medsuite/models';

/**
 * Investment with relations type (extended from API response)
 */
interface InvestmentWithRelations extends Investment {
  portco_name?: string;
  company_name?: string;
  segment_name?: string;
  program_name?: string;
  composite_score?: number | null;
}

/**
 * Cost data for context
 */
interface CostData {
  cost_category_name?: string;
  description?: string;
  capex_cost: number;
  opex_cost: number;
}

/**
 * Benefit data for context
 */
interface BenefitData {
  measure_type_name?: string;
  description?: string;
  amount?: number | null;
  amount_type?: string;
}

/**
 * Sponsor data for context
 */
interface SponsorData {
  sponsor_type_name?: string;
  sponsor_name: string;
}

/**
 * Resource data for context
 */
interface ResourceData {
  resource_type: string;
  resource_name: string;
  project_role?: string | null;
  resource_allocation?: number | null;
  quantifier?: string | null;
  temporary_or_perpetual?: string | null;
  resource_start_date?: Date | string | null;
  resource_end_date?: Date | string | null;
}

/**
 * Milestone data for context
 */
interface MilestoneData {
  milestone_id: number;
  title: string;
  sequence?: number | null;
  expected_date?: Date | string | null;
  actual_date?: Date | string | null;
}

/**
 * Investment with full details for AI context
 */
interface InvestmentWithDetails extends InvestmentWithRelations {
  costs?: CostData[];
  benefits?: BenefitData[];
  sponsors?: SponsorData[];
  resources?: ResourceData[];
  totalCosts?: { capex: number; opex: number; total: number };
  totalBenefits?: number;
}

/**
 * Hook return type
 */
export interface UseInvestmentContextReturn {
  investmentId: number | null;
  investment: InvestmentWithRelations | null;
  isLoading: boolean;
  hasContext: boolean;
  contextString: string | null;
  contextType: 'single' | 'list' | 'none';
}

/**
 * Format currency amount
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format date for context
 */
function formatDate(date: Date | string | null | undefined): string {
  if (!date) {
    return 'Not set';
  }
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Maximum extracted text per document in AI context (5KB)
 */
const MAX_EXTRACTED_TEXT_PER_DOC = 5 * 1024;

/**
 * Build a context string from single investment data
 */
function buildSingleContextString(
  investment: InvestmentWithRelations,
  milestones: MilestoneData[] = [],
  costs: CostData[] = [],
  benefits: BenefitData[] = [],
  attachmentTexts: AttachmentExtractedText[] = []
): string {
  const lines: string[] = [
    `Investment: ${investment.title}`,
    `ID: ${investment.investment_id}`,
    `Status: ${investment.status}`,
  ];

  if (investment.portco_name) {
    lines.push(`PortCo: ${investment.portco_name}`);
  }

  if (investment.segment_name) {
    lines.push(`Segment: ${investment.segment_name}`);
  }

  lines.push(`Regulatory Requirement: ${investment.is_regulatory_requirement ? 'Yes' : 'No'}`);

  if (investment.regulatory_details) {
    lines.push(`Regulatory Details: ${investment.regulatory_details}`);
  }

  if (investment.prime_objective) {
    lines.push(`Objective: ${investment.prime_objective}`);
  }

  if (investment.problem_context) {
    lines.push(`Problem Context: ${investment.problem_context}`);
  }

  if (investment.improvement_ideas) {
    lines.push(`Improvement Ideas: ${investment.improvement_ideas}`);
  }

  if (investment.assumptions) {
    lines.push(`Assumptions: ${investment.assumptions}`);
  }

  if (investment.composite_score !== null && investment.composite_score !== undefined) {
    lines.push(`Composite Score: ${investment.composite_score}`);
  }

  // Add milestones section
  if (milestones.length > 0) {
    lines.push('');
    lines.push('=== KEY MILESTONES ===');

    // Sort milestones by sequence then by expected date
    const sortedMilestones = [...milestones].sort((a, b) => {
      if (
        a.sequence !== null &&
        b.sequence !== null &&
        a.sequence !== undefined &&
        b.sequence !== undefined
      ) {
        return a.sequence - b.sequence;
      }
      if (a.expected_date && b.expected_date) {
        return new Date(a.expected_date).getTime() - new Date(b.expected_date).getTime();
      }
      return 0;
    });

    // Find earliest and latest dates for summary
    const expectedDates = sortedMilestones
      .map((m) => m.expected_date)
      .filter((d): d is Date | string => d !== null && d !== undefined)
      .map((d) => new Date(d).getTime());

    if (expectedDates.length > 0) {
      const earliestDate = new Date(Math.min(...expectedDates));
      const latestDate = new Date(Math.max(...expectedDates));
      const durationDays = Math.round(
        (latestDate.getTime() - earliestDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      lines.push(`Project Start (earliest milestone): ${formatDate(earliestDate)}`);
      lines.push(`Project End (latest milestone): ${formatDate(latestDate)}`);
      lines.push(
        `Total Duration: ${durationDays} days (approximately ${Math.round(durationDays / 7)} weeks or ${Math.round(durationDays / 30)} months)`
      );
      lines.push('');
    }

    lines.push('Milestones:');
    sortedMilestones.forEach((milestone, index) => {
      const seq =
        milestone.sequence !== null && milestone.sequence !== undefined
          ? milestone.sequence
          : index + 1;
      const expectedStr = milestone.expected_date
        ? formatDate(milestone.expected_date)
        : 'No date set';
      const actualStr = milestone.actual_date
        ? ` (Actual: ${formatDate(milestone.actual_date)})`
        : '';
      lines.push(`  ${seq}. ${milestone.title} - Expected: ${expectedStr}${actualStr}`);
    });
  }

  // Add costs section
  if (costs.length > 0) {
    lines.push('');
    lines.push('=== INVESTMENT COSTS ===');

    // Calculate totals
    let totalCapex = 0;
    let totalOpex = 0;
    costs.forEach((cost) => {
      totalCapex += cost.capex_cost || 0;
      totalOpex += cost.opex_cost || 0;
    });
    const totalCost = totalCapex + totalOpex;

    lines.push(`Total CapEx: ${formatCurrency(totalCapex)}`);
    lines.push(`Total OpEx: ${formatCurrency(totalOpex)}`);
    lines.push(`Total Investment Cost: ${formatCurrency(totalCost)}`);
    lines.push('');

    lines.push('Cost Breakdown:');
    costs.forEach((cost) => {
      const costTotal = (cost.capex_cost || 0) + (cost.opex_cost || 0);
      if (costTotal > 0) {
        const category = cost.cost_category_name || 'Uncategorized';
        const desc = cost.description ? ` - ${cost.description}` : '';
        lines.push(
          `  - ${category}: ${formatCurrency(costTotal)} (CapEx: ${formatCurrency(cost.capex_cost || 0)}, OpEx: ${formatCurrency(cost.opex_cost || 0)})${desc}`
        );
      }
    });
  }

  // Add benefits section
  if (benefits.length > 0) {
    lines.push('');
    lines.push('=== EXPECTED BENEFITS ===');

    // Separate quantitative and qualitative benefits
    const quantitativeBenefits = benefits.filter((b) => b.amount && b.amount > 0);
    const qualitativeBenefits = benefits.filter((b) => !b.amount || b.amount === 0);

    // Calculate total quantitative benefits
    const totalBenefitsAmount = quantitativeBenefits.reduce((sum, b) => sum + (b.amount || 0), 0);

    if (totalBenefitsAmount > 0) {
      lines.push(`Total Quantifiable Benefits: ${formatCurrency(totalBenefitsAmount)}`);
      lines.push('');
    }

    if (quantitativeBenefits.length > 0) {
      lines.push('Quantitative Benefits:');
      quantitativeBenefits.forEach((benefit) => {
        const measureType = benefit.measure_type_name || 'Benefit';
        const amountType = benefit.amount_type ? ` (${benefit.amount_type})` : '';
        const desc = benefit.description ? ` - ${benefit.description}` : '';
        lines.push(
          `  - ${measureType}: ${formatCurrency(benefit.amount || 0)}${amountType}${desc}`
        );
      });
    }

    if (qualitativeBenefits.length > 0) {
      if (quantitativeBenefits.length > 0) {
        lines.push('');
      }
      lines.push('Qualitative Benefits:');
      qualitativeBenefits.forEach((benefit) => {
        const measureType = benefit.measure_type_name || 'Benefit';
        const desc = benefit.description || 'No description';
        lines.push(`  - ${measureType}: ${desc}`);
      });
    }

    // ROI calculation if we have both costs and quantitative benefits
    if (costs.length > 0 && totalBenefitsAmount > 0) {
      const totalCost = costs.reduce((sum, c) => sum + (c.capex_cost || 0) + (c.opex_cost || 0), 0);
      if (totalCost > 0) {
        const roi = ((totalBenefitsAmount - totalCost) / totalCost) * 100;
        lines.push('');
        lines.push(`Projected ROI: ${roi.toFixed(1)}%`);
        lines.push(`Net Benefit: ${formatCurrency(totalBenefitsAmount - totalCost)}`);
      }
    }
  }

  // Add attached documents section
  const docsWithContent = attachmentTexts.filter(
    (doc) => doc.extraction_status === 'completed' && doc.extracted_text
  );

  if (docsWithContent.length > 0) {
    lines.push('');
    lines.push('=== ATTACHED DOCUMENTS ===');
    lines.push(`${docsWithContent.length} document(s) with extracted content:`);
    lines.push('');

    docsWithContent.forEach((doc, index) => {
      lines.push(`--- Document ${index + 1}: ${doc.title} (${doc.filename}) ---`);
      if (doc.extracted_text) {
        // Truncate long documents
        const text =
          doc.extracted_text.length > MAX_EXTRACTED_TEXT_PER_DOC
            ? doc.extracted_text.substring(0, MAX_EXTRACTED_TEXT_PER_DOC) +
              '\n\n[Document truncated for context size...]'
            : doc.extracted_text;
        lines.push(text);
      }
      lines.push('');
    });
  }

  return lines.join('\n');
}

/**
 * Build a context string from investments list with full details
 */
function buildListContextString(investments: InvestmentWithDetails[]): string {
  const lines: string[] = [];

  // Summary stats
  lines.push(`=== INVESTMENTS PORTFOLIO SUMMARY ===`);
  lines.push(`Total Investments: ${investments.length}`);
  lines.push('');

  // Status breakdown
  const statusCounts: Record<string, number> = {};
  investments.forEach((inv) => {
    statusCounts[inv.status] = (statusCounts[inv.status] || 0) + 1;
  });

  lines.push('Status Breakdown:');
  Object.entries(statusCounts)
    .sort((a, b) => b[1] - a[1])
    .forEach(([status, count]) => {
      lines.push(`  - ${status}: ${count}`);
    });
  lines.push('');

  // PortCo breakdown
  const portcoCounts: Record<string, number> = {};
  investments.forEach((inv) => {
    if (inv.portco_name) {
      portcoCounts[inv.portco_name] = (portcoCounts[inv.portco_name] || 0) + 1;
    }
  });

  if (Object.keys(portcoCounts).length > 0) {
    lines.push('Portfolio Companies:');
    Object.entries(portcoCounts)
      .sort((a, b) => b[1] - a[1])
      .forEach(([portco, count]) => {
        lines.push(`  - ${portco}: ${count} investments`);
      });
    lines.push('');
  }

  // Segment breakdown
  const segmentCounts: Record<string, number> = {};
  investments.forEach((inv) => {
    if (inv.segment_name) {
      segmentCounts[inv.segment_name] = (segmentCounts[inv.segment_name] || 0) + 1;
    }
  });

  if (Object.keys(segmentCounts).length > 0) {
    lines.push('Segments:');
    Object.entries(segmentCounts)
      .sort((a, b) => b[1] - a[1])
      .forEach(([segment, count]) => {
        lines.push(`  - ${segment}: ${count} investments`);
      });
    lines.push('');
  }

  // Portfolio-wide financial summary
  let totalCapex = 0;
  let totalOpex = 0;
  let totalBenefits = 0;
  investments.forEach((inv) => {
    if (inv.totalCosts) {
      totalCapex += inv.totalCosts.capex;
      totalOpex += inv.totalCosts.opex;
    }
    if (inv.totalBenefits) {
      totalBenefits += inv.totalBenefits;
    }
  });

  if (totalCapex > 0 || totalOpex > 0 || totalBenefits > 0) {
    lines.push('=== PORTFOLIO FINANCIAL SUMMARY ===');
    lines.push(`Total CapEx: ${formatCurrency(totalCapex)}`);
    lines.push(`Total OpEx: ${formatCurrency(totalOpex)}`);
    lines.push(`Total Investment: ${formatCurrency(totalCapex + totalOpex)}`);
    lines.push(`Total Expected Benefits: ${formatCurrency(totalBenefits)}`);
    if (totalCapex + totalOpex > 0) {
      const roi = ((totalBenefits - (totalCapex + totalOpex)) / (totalCapex + totalOpex)) * 100;
      lines.push(`Projected ROI: ${roi.toFixed(1)}%`);
    }
    lines.push('');
  }

  // List of investments (sorted by composite score)
  const sortedInvestments = [...investments].sort((a, b) => {
    const scoreA = a.composite_score ?? -1;
    const scoreB = b.composite_score ?? -1;
    return scoreB - scoreA;
  });

  lines.push('=== ALL INVESTMENTS (DETAILED) ===');
  sortedInvestments.forEach((inv, index) => {
    lines.push(`${index + 1}. ${inv.title}`);
    lines.push(
      `   ID: ${inv.investment_id} | Status: ${inv.status} | Score: ${inv.composite_score ?? 'N/A'}`
    );
    if (inv.portco_name) {
      lines.push(`   PortCo: ${inv.portco_name}`);
    }
    if (inv.segment_name) {
      lines.push(`   Segment: ${inv.segment_name}`);
    }
    if (inv.prime_objective) {
      const objective =
        inv.prime_objective.length > 150
          ? inv.prime_objective.substring(0, 150) + '...'
          : inv.prime_objective;
      lines.push(`   Objective: ${objective}`);
    }

    // Sponsors
    if (inv.sponsors && inv.sponsors.length > 0) {
      lines.push(`   Sponsors:`);
      inv.sponsors.forEach((s) => {
        lines.push(`     - ${s.sponsor_type_name || 'Sponsor'}: ${s.sponsor_name}`);
      });
    }

    // Costs summary
    if (inv.totalCosts && (inv.totalCosts.capex > 0 || inv.totalCosts.opex > 0)) {
      lines.push(
        `   Costs: CapEx ${formatCurrency(inv.totalCosts.capex)}, OpEx ${formatCurrency(inv.totalCosts.opex)}, Total ${formatCurrency(inv.totalCosts.total)}`
      );
      if (inv.costs && inv.costs.length > 0) {
        inv.costs.forEach((c) => {
          if (c.capex_cost > 0 || c.opex_cost > 0) {
            const costLine = `     - ${c.cost_category_name || 'Cost'}: CapEx ${formatCurrency(c.capex_cost)}, OpEx ${formatCurrency(c.opex_cost)}`;
            lines.push(costLine);
          }
        });
      }
    }

    // Benefits summary
    if (inv.benefits && inv.benefits.length > 0) {
      lines.push(`   Benefits (${inv.benefits.length} items):`);
      inv.benefits.forEach((b) => {
        const amountStr = b.amount ? formatCurrency(b.amount) : 'Non-quantitative';
        const desc = b.description
          ? b.description.length > 80
            ? b.description.substring(0, 80) + '...'
            : b.description
          : '';
        lines.push(
          `     - ${b.measure_type_name || 'Benefit'}: ${amountStr}${desc ? ' - ' + desc : ''}`
        );
      });
      if (inv.totalBenefits && inv.totalBenefits > 0) {
        lines.push(`   Total Benefits Value: ${formatCurrency(inv.totalBenefits)}`);
      }
    }

    // Resources summary
    if (inv.resources && inv.resources.length > 0) {
      lines.push(`   Resources (${inv.resources.length} items):`);
      inv.resources.forEach((r) => {
        const allocation = r.resource_allocation ? `${r.resource_allocation}%` : '';
        const role = r.project_role || '';
        const duration =
          r.temporary_or_perpetual === 'T'
            ? 'Temporary'
            : r.temporary_or_perpetual === 'P'
              ? 'Perpetual'
              : '';
        const details = [role, allocation, duration].filter(Boolean).join(', ');
        lines.push(
          `     - ${r.resource_type}: ${r.resource_name}${details ? ' (' + details + ')' : ''}`
        );
      });
    }

    lines.push('');
  });

  return lines.join('\n');
}

/**
 * Hook for extracting investment context from URL
 *
 * Detects when user is viewing or editing an investment and provides
 * that context for the AI chat assistant.
 * Also provides list context when on the investments home page.
 */
export function useInvestmentContext(): UseInvestmentContextReturn {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // Determine page type
  const isHomePage = location.pathname === '/' || location.pathname === '/investments';
  const isSingleInvestmentPage = location.pathname.includes('/investments/') && id;
  const investmentId = id ? parseInt(id, 10) : null;
  const isValidId = investmentId !== null && !isNaN(investmentId) && investmentId > 0;

  // Fetch single investment data if on investment detail/edit page
  const { data: investment, isLoading: isLoadingSingle } = useInvestment(investmentId ?? 0, {
    enabled: isValidId && !!isSingleInvestmentPage,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch milestones for single investment page
  const { data: milestones = [], isLoading: isLoadingMilestones } = useMilestones(
    investmentId ?? 0,
    {
      enabled: isValidId && !!isSingleInvestmentPage,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  // Fetch costs for single investment page
  const { data: singleCosts = [], isLoading: isLoadingCosts } = useCosts(investmentId ?? 0, {
    enabled: isValidId && !!isSingleInvestmentPage,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch benefits for single investment page
  const { data: singleBenefits = [], isLoading: isLoadingBenefits } = useBenefits(
    investmentId ?? 0,
    {
      enabled: isValidId && !!isSingleInvestmentPage,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  // Fetch extracted texts from attachments for single investment page
  const { data: extractedTextsData, isLoading: isLoadingExtractedTexts } = useExtractedTexts(
    investmentId ?? 0,
    {
      enabled: isValidId && !!isSingleInvestmentPage,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );
  const attachmentTexts = useMemo(
    () => extractedTextsData?.documents ?? [],
    [extractedTextsData]
  );

  // Fetch all investments if on home page
  const { data: investments, isLoading: isLoadingList } = useInvestments(undefined, {
    enabled: isHomePage,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch costs, benefits, and sponsors for all investments on home page
  const investmentIds = useMemo(() => {
    if (!isHomePage || !investments) {
      return [];
    }
    return investments.map((inv) => inv.investment_id);
  }, [isHomePage, investments]);

  // Batch fetch costs for all investments
  const costsQueries = useQueries({
    queries: investmentIds.map((invId) => ({
      queryKey: costKeys.list(invId),
      queryFn: async () => {
        const response = await costsService.getAll(invId);
        const responseData = (response.data as unknown as Record<string, unknown>).data || response.data;
        const wrappedData = responseData as Record<string, unknown>;
        const costs = (wrappedData.costs || wrappedData || []) as Record<string, unknown>[];
        return costs.map((cost: Record<string, unknown>) => ({
          ...cost,
          capex_cost: parseFloat(String(cost.capex_cost)) || 0,
          opex_cost: parseFloat(String(cost.opex_cost)) || 0,
        }));
      },
      enabled: isHomePage && investmentIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    })),
  });

  // Batch fetch benefits for all investments
  const benefitsQueries = useQueries({
    queries: investmentIds.map((invId) => ({
      queryKey: benefitKeys.list(invId),
      queryFn: async () => {
        const response = await benefitsService.getAll(invId);
        const responseData = (response.data as unknown as Record<string, unknown>).data || response.data;
        const wrappedData = responseData as Record<string, unknown>;
        const benefits = (wrappedData.benefits || wrappedData || []) as Record<string, unknown>[];
        return benefits.map((benefit: Record<string, unknown>) => ({
          ...benefit,
          amount: benefit.amount ? parseFloat(String(benefit.amount)) : null,
        }));
      },
      enabled: isHomePage && investmentIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    })),
  });

  // Batch fetch sponsors for all investments
  const sponsorsQueries = useQueries({
    queries: investmentIds.map((invId) => ({
      queryKey: sponsorKeys.list(invId),
      queryFn: async () => {
        const response = await sponsorsService.getAll(invId);
        const responseData = (response.data as unknown as Record<string, unknown>).data || response.data;
        const wrappedData = responseData as Record<string, unknown>;
        return (wrappedData.sponsors || wrappedData || []) as Record<string, unknown>[];
      },
      enabled: isHomePage && investmentIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    })),
  });

  // Batch fetch resources for all investments
  const resourcesQueries = useQueries({
    queries: investmentIds.map((invId) => ({
      queryKey: resourceKeys.list(invId),
      queryFn: async () => {
        const response = await resourcesService.getAll(invId);
        const responseData = (response.data as unknown as Record<string, unknown>).data || response.data;
        const wrappedData = responseData as Record<string, unknown>;
        return (wrappedData.resources || wrappedData || []) as Record<string, unknown>[];
      },
      enabled: isHomePage && investmentIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    })),
  });

  // Merge investment data with costs, benefits, sponsors, and resources
  const investmentsWithDetails = useMemo((): InvestmentWithDetails[] => {
    if (!investments) {
      return [];
    }

    return investments.map((inv, index) => {
      const costs = costsQueries[index]?.data || [];
      const benefits = benefitsQueries[index]?.data || [];
      const sponsors = sponsorsQueries[index]?.data || [];
      const resources = resourcesQueries[index]?.data || [];

      // Calculate totals
      const totalCapex = costs.reduce((sum: number, c: CostData) => sum + (c.capex_cost || 0), 0);
      const totalOpex = costs.reduce((sum: number, c: CostData) => sum + (c.opex_cost || 0), 0);
      const totalBenefitsAmount = benefits.reduce(
        (sum: number, b: BenefitData) => sum + (b.amount || 0),
        0
      );

      return {
        ...inv,
        costs,
        benefits,
        sponsors,
        resources,
        totalCosts: {
          capex: totalCapex,
          opex: totalOpex,
          total: totalCapex + totalOpex,
        },
        totalBenefits: totalBenefitsAmount,
      } as unknown as InvestmentWithDetails;
    });
  }, [investments, costsQueries, benefitsQueries, sponsorsQueries, resourcesQueries]);

  // Check if all detail queries are done loading
  const isLoadingDetails =
    costsQueries.some((q) => q.isLoading) ||
    benefitsQueries.some((q) => q.isLoading) ||
    sponsorsQueries.some((q) => q.isLoading) ||
    resourcesQueries.some((q) => q.isLoading);

  // Determine context type and build context string
  const { contextString, contextType } = useMemo(() => {
    // Single investment context takes priority
    if (isSingleInvestmentPage && investment) {
      return {
        contextString: buildSingleContextString(
          investment as InvestmentWithRelations,
          milestones as MilestoneData[],
          singleCosts as CostData[],
          singleBenefits as BenefitData[],
          attachmentTexts
        ),
        contextType: 'single' as const,
      };
    }

    // List context for home page - use enhanced data with details
    if (isHomePage && investmentsWithDetails && investmentsWithDetails.length > 0) {
      return {
        contextString: buildListContextString(investmentsWithDetails),
        contextType: 'list' as const,
      };
    }

    return { contextString: null, contextType: 'none' as const };
  }, [
    investment,
    milestones,
    singleCosts,
    singleBenefits,
    attachmentTexts,
    investmentsWithDetails,
    isSingleInvestmentPage,
    isHomePage,
  ]);

  const isLoading =
    isLoadingSingle ||
    isLoadingMilestones ||
    isLoadingCosts ||
    isLoadingBenefits ||
    isLoadingExtractedTexts ||
    isLoadingList ||
    (isHomePage && isLoadingDetails);
  const hasContext = contextString !== null;

  return {
    investmentId: isValidId ? investmentId : null,
    investment: (investment as InvestmentWithRelations) || null,
    isLoading,
    hasContext,
    contextString,
    contextType,
  };
}

export default useInvestmentContext;
